/**
 * The one status vocabulary (design-system/MEANING.md, Colour): every component that colours a
 * status takes `tone: Tone`. neutral = a fact, info = information or in progress, positive = good
 * for the reader, attention = needs a look or went wrong. Never yellow (that is for the one action
 * or figure a screen is about). Palette note (2026-10-12): positive shares the forest green with
 * selection, and attention has no hue (it resolves to ink on elevated), so both must carry an icon
 * and words: Chip, Badge and InlineAlert add the glyph themselves; callers of the raw maps must too.
 */
export type Tone = 'neutral' | 'info' | 'positive' | 'attention';

/** Soft fill with darkened text: chips, trend chips, soft badges, icon discs, fact tiles. */
export const TONE_SOFT: Record<Tone, string> = {
  neutral: 'bg-jc-elevated text-jc-ink-secondary',
  info: 'bg-jc-forest-soft text-jc-accent',
  positive: 'bg-jc-forest-soft text-jc-accent',
  attention: 'bg-jc-elevated text-jc-ink',
};

/** Solid fill: badges that must stand out on a card. */
export const TONE_SOLID: Record<Tone, string> = {
  neutral: 'bg-jc-ink text-jc-page',
  // Solid forest with its own on-forest text in both themes (accent is a light green in dark, too pale under white text).
  info: 'bg-jc-forest text-jc-on-forest',
  positive: 'bg-jc-forest text-jc-on-forest',
  attention: 'bg-jc-ink text-jc-page',
};

/** The strong colour as a fill: status dots. */
export const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-jc-ink-secondary',
  // Dots are small: the theme-aware accent stays visible on dark cards (forest itself is dark there).
  info: 'bg-jc-accent',
  positive: 'bg-jc-accent',
  attention: 'bg-jc-ink',
};

/** The tone as text colour: icons in notes and toasts. */
export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-jc-ink-secondary',
  info: 'text-jc-accent',
  positive: 'text-jc-accent',
  attention: 'text-jc-ink',
};
